import { toUrnString } from "@/types/urn.ts";
import type { OmniArtifact } from "./selectOmniArtifacts.ts";

/**
 * Where the omni-artifact carousel sits, given the id the user navigated to and
 * the URN the canvas is presenting.
 *
 * Position is tracked by artifact id rather than index so the view stays put as
 * the surfaced set mutates. Resolution order:
 *
 * 1. `activeId` — the artifact the user navigated to, while it's still surfaced.
 * 2. The artifact the canvas is presenting. The agent can target a card
 *    server-side (`thread_canvas:update`) and that target need not be the first
 *    in the queue, so a thread can load with the second artifact on the canvas;
 *    the biscuit has to describe the same one it's showing.
 * 3. The top of the queue.
 *
 * Matching on the full URN (not the bare id) keeps a same-id contact or source on
 * the canvas from being mistaken for a surfaced artifact.
 */
export const resolveActiveOmniIndex = (
  artifacts: readonly OmniArtifact[],
  activeId: string | undefined,
  presentedUrn: string | undefined,
): number => {
  const activeIndex = artifacts.findIndex((a) => a.id === activeId);
  if (activeIndex >= 0) return activeIndex;
  const presentedIndex = presentedUrn
    ? artifacts.findIndex((a) => toUrnString({ type: a.artifactType, id: a.id }) === presentedUrn)
    : -1;
  return Math.max(0, presentedIndex);
};
